import * as AvatarPrimitive from '@radix-ui/react-avatar'

import { cn } from '@/lib/utils/cn'

/**
 * A person's avatar: their photo when there is one, their initials when there is not.
 *
 * Radix handles the part that is easy to get wrong — it only swaps the image in once it has
 * actually loaded, so a slow or blocked third-party avatar shows initials rather than a
 * broken-image icon or a flash of empty circle.
 *
 * Decorative by default. It always sits beside the person's name, and announcing "GB,
 * Govind Barde" to a screen reader says the same thing twice.
 */
export function Avatar({
  name,
  src,
  size = 'md',
  className,
}: {
  name: string
  src?: string | null
  size?: 'sm' | 'md' | 'lg'
  className?: string
}) {
  const sizes = {
    sm: 'h-7 w-7 text-[11px]',
    md: 'h-8 w-8 text-xs',
    lg: 'h-12 w-12 text-base',
  }
  return (
    <AvatarPrimitive.Root
      aria-hidden="true"
      className={cn(
        'inline-flex shrink-0 select-none items-center justify-center overflow-hidden rounded-full',
        'border border-line bg-active font-semibold text-ink',
        sizes[size],
        className,
      )}
    >
      {src ? (
        <AvatarPrimitive.Image
          src={src}
          alt=""
          referrerPolicy="no-referrer"
          className="h-full w-full object-cover"
        />
      ) : null}
      <AvatarPrimitive.Fallback delayMs={src ? 400 : 0}>{initials(name)}</AvatarPrimitive.Fallback>
    </AvatarPrimitive.Root>
  )
}

export function initials(name: string): string {
  const parts = name.split(/[\s._@-]+/).filter(Boolean)
  const first = parts[0]?.[0] ?? '?'
  const second = parts.length > 1 ? (parts[1]?.[0] ?? '') : ''
  return (first + second).toUpperCase()
}
